<script lang="ts">
export default {
  name: 'DOBenchmarking'
}
</script>

<script setup lang="ts">
import { benchmarkService, referenceService } from '@/services/api';
import { Account } from '@/types/Account';
import { Benchmark, DOBenchmark, emptyBenchmark } from '@/types/Benchmark';
import { ref, computed, onMounted, watch } from 'vue';
import { AttributeOption, both, calc, Chart, Column, PUBLIC, sameOwnershipType, SHOW_ALL } from './Benchmarking';
import BenchmarkingCharts from './BenchmarkingCharts.vue';
import BenchmarkingTable from './BenchmarkingTable.vue';
import ClientFilter from '@/components/ClientFilter.vue';
import { compactMoney } from '@/utilities/Money';
import AccountBenchmarkViewOptions from '@/components/AccountBenchmarkViewOptions.vue';
import { calcAverage, calcMedian } from '@/utilities/Number';
import GenericFilter from '@/components/GenericFilter.vue';
import { Industry } from '@/types/Industry';
import { LabelValue } from '@/types/label-value';
import { useRoute } from 'vue-router';
import FilterSlider from '@/components/FilterSlider.vue';

const props = defineProps<{
  account: Account
}>();

const route = useRoute();

// -----
// data
// -----
const isLoading = ref(false);
const accountData = ref<DOBenchmark[]>([]);
const data = ref( [] as DOBenchmark[]);
const selectedClients = ref( [] as string[]);
const appliedClients = ref( [] as string[]);
const appliedLabelDisplay = ref( SHOW_ALL);
const appliedMarketCapRange = ref( undefined as (undefined | number[]));
const appliedCompanyAssetsRange = ref( undefined as (undefined | number[]));
const revenueFilterRange = ref( undefined as (undefined | number[]));
const attributeTypes = ref( [] as AttributeOption[]);
const selectedAttributeTypes = ref( [] as number[]);
const industries = ref( [] as LabelValue[]);
const selectedIndustries = ref( [] as number[]);

function transformAccountName(d: DOBenchmark): DOBenchmark {
  return ({ ...d, accountName: appliedLabelDisplay.value === SHOW_ALL ? d.accountName : d.accountNameAlt });
}

function filterByMarketCapRange(d: DOBenchmark): boolean {
  if (appliedMarketCapRange.value) {
    const min = appliedMarketCapRange.value[0];
    const max = appliedMarketCapRange.value[1];
    return d.marketCap2YearHigh >= min && d.marketCap2YearHigh <= max;
  }
  return true;
}

function filterByCompanyAssetsRange(d: DOBenchmark): boolean {
  if (appliedCompanyAssetsRange.value) {
    if (d.companyAssets === undefined) return false;

    const min = appliedCompanyAssetsRange.value[0];
    const max = appliedCompanyAssetsRange.value[1];
    return d.companyAssets >= min && d.companyAssets <= max;
  }
  return true;
}

function filterByRevenueRange(d: DOBenchmark): boolean {
  if (revenueFilterRange.value) {
    if (d.revenueTTM === undefined) return false;

    const min = revenueFilterRange.value[0];
    const max = revenueFilterRange.value[1];
    return d.revenueTTM >= min && d.revenueTTM <= max;
  }
  return true;
}

function applyClientFilter(): void {
  appliedClients.value = selectedClients.value;
}

function applyViewOptions(selectedLabels: string): void {
  appliedLabelDisplay.value = selectedLabels;
}

async function refresh(): Promise<void> {
  isLoading.value = true;

  const [benchmarkData, attrTypes, industriesRefData] = await Promise.all([
    benchmarkService.dno(props.account.ownershipType, selectedAttributeTypes.value, selectedIndustries.value),
    referenceService.accountAttributeTypes(),
    referenceService.industries(),
  ]);

  attributeTypes.value = attrTypes
    .filter(t => sameOwnershipType(t, props.account.ownershipType) || both(t))
    .map(t => ({ label: t.name, value: t.id }));
  data.value = (benchmarkData || []).map((d, i) => ({ ...d, packagedCoverageTypes: `Packaged with ${d.packagedCoverageTypes}`, accountNameAlt: `Company ${i + 1}` }));
  industries.value = industriesRefData.map((i: Industry) => ({ label: i.name, value: i.ID }));

  appliedMarketCapRange.value = undefined;

  // upon first load, save off the accountData to populate the first row of the benchmarking table

  if (accountData.value.length === 0) {
    const match = data.value.filter(d => d.accountId === route.params.id);

    if (match.length > 0) {
      const account = match.reduce((obj, d) => {
        if (!obj.policyYear) return d;
        if (!d.policyYear) return obj;
        if (obj.policyYear < d.policyYear) obj = d;
        return obj;
      }, { ...match[0] });
      accountData.value = [{ ...account, frozen: true }];
    }
  }

  isLoading.value = false;
}

function marketCapChanged(range: number[]): void {
  // the FilterSlider :values is represented in millions data model is not -> converting it back
  appliedMarketCapRange.value = range.length === 2 ? [range[0] * 1000000, range[1] * 1000000] : undefined;
}

function companyAssetsChanged(range: number[]): void {
  appliedCompanyAssetsRange.value = range.length === 2 ? [...range] : undefined;
}

function revenueChanged(range: number[]): void {
  revenueFilterRange.value = range.length === 2 ? [...range] : undefined;
}

function normalize(val: string): string {
  return val.trim().toLowerCase();
}

// ----------
// computed
// ----------

const marketCapRange = computed((): number[] => {
  const vals = data.value.filter(d => d.marketCap2YearHigh).map(d => d.marketCap2YearHigh);
  return [0, vals.length === 0 ? 0 : Math.ceil(Math.max(...vals) / 1_000_000)];
});

const revenueRange = computed((): number[] => {
  const vals = data.value.filter(d => d.revenueTTM).map(d => d.revenueTTM);
  return [0, vals.length === 0 ? 0 : Math.max(...vals)];
});

const assetRange = computed((): number[] => {
  const vals = data.value.filter(d => d.companyAssets).map(d => d.companyAssets) as number[];
  return [0, vals.length === 0 ? 0 : Math.max(...vals)];
});

const clients = computed((): { text: string, value: string }[] => {
  const clients = data.value.reduce((acc, d) => {

    // is it the current client?
    if (d.accountId === route.params.id) return acc;

    // already in accumulator?
    if (acc.some(i => d.accountName && normalize(i.text) === normalize(d.accountName))) {
      return acc;
    }
    if (props.account.ownershipType !== d.ownershipType) {
      return acc;
    }

    acc.push({ text: d.accountName || '', value: d.accountName || '' });
    return acc;
  }, [] as { text: string, value: string }[]);

  clients.sort((a, b) => a.text.localeCompare(b.text));
  return clients;
});

const otherData = computed((): DOBenchmark[] => {
  if (appliedClients.value.length > 0) {
    return data.value
      .filter(d => appliedClients.value.includes(d.accountName || ''))
      .filter(d => ('' + d.ownershipType).trim().toLowerCase() === ('' + props.account.ownershipType).trim().toLowerCase())
      .filter(filterByMarketCapRange)
      .filter(filterByRevenueRange)
      .filter(filterByCompanyAssetsRange)
      .map(transformAccountName) as DOBenchmark[];
  }
  return data.value
    .filter(d => d.accountId !== route.params.id)
    .filter(d => ('' + d.ownershipType).trim().toLowerCase() === ('' + props.account.ownershipType).trim().toLowerCase())
    .filter(filterByMarketCapRange)
    .filter(filterByRevenueRange)
    .filter(filterByCompanyAssetsRange)
    .map(transformAccountName) as DOBenchmark[];
});

const averageData = computed((): DOBenchmark[] => {
  return [{
    ...emptyBenchmark,
    key: 'avg',
    industry: 'Average',
    marketCap2YearHigh: calc(otherData.value, 'marketCap2YearHigh', calcAverage),
    marketCapMostRecent: calc(otherData.value, 'marketCapMostRecent', calcAverage),
    revenueTTM: calc(otherData.value, 'revenueTTM', calcAverage),
    companyAssets: calc(otherData.value, 'companyAssets', calcAverage),
    totalRetention: calc(otherData.value, 'totalRetention', calcAverage, true),
    totalDOLimit: calc(otherData.value, 'totalDOLimit', calcAverage),
    totalABCLimit: calc(otherData.value, 'totalABCLimit', calcAverage),
    totalSideA: calc(otherData.value, 'totalSideA', calcAverage),
    totalPrimaryLimit: calc(otherData.value, 'totalPrimaryLimit', calcAverage),
    maxPrimaryPPMAnnualized: calc(otherData.value, 'maxPrimaryPPMAnnualized', calcAverage),
    firstLayerILF: calc(otherData.value, 'firstLayerILF', calcAverage)
  }];
});

const medianData = computed((): DOBenchmark[] => {
  return [{
    ...emptyBenchmark,
    key: 'med',
    industry: 'Median',
    marketCap2YearHigh: calc(otherData.value, 'marketCap2YearHigh', calcMedian),
    marketCapMostRecent: calc(otherData.value, 'marketCapMostRecent', calcMedian),
    revenueTTM: calc(otherData.value, 'revenueTTM', calcMedian),
    companyAssets: calc(otherData.value, 'companyAssets', calcMedian),
    totalRetention: calc(otherData.value, 'totalRetention', calcMedian, true),
    totalDOLimit: calc(otherData.value, 'totalDOLimit', calcMedian),
    totalABCLimit: calc(otherData.value, 'totalABCLimit', calcMedian),
    totalSideA: calc(otherData.value, 'totalSideA', calcMedian),
    totalPrimaryLimit: calc(otherData.value, 'totalPrimaryLimit', calcMedian),
    maxPrimaryPPMAnnualized: calc(otherData.value, 'maxPrimaryPPMAnnualized', calcMedian),
    firstLayerILF: calc(otherData.value, 'firstLayerILF', calcMedian)
  }];
});

const isPublic = computed((): boolean => {
  return props.account.ownershipType?.trim().toLowerCase() === PUBLIC;
});

const isPrivate = computed((): boolean => {
  return !isPublic.value;
});

const titleText = computed((): string => {
  return `${isPublic.value ? 'Public' : 'Private'} D&O Benchmarking`;
});

const columns = computed((): Column<DOBenchmark>[] => {
  const columns: (Column<DOBenchmark> | undefined)[] = [
    { fieldName: 'policyYear', type: 'number', title: 'Year' },
    { fieldName: 'accountName', type: 'nosort', title: 'Client', classes: 'truncated', url: (data) => `/account/${data.accountId}/overview`, showInfoIcon: 'isPackaged', showInfoFieldName: 'packagedCoverageTypes' },
    { fieldName: 'industry', type: '', title: 'Industry', url: (data) => `/account/${data.accountId}/edit`, accessoryIcon: 'pencil' },
    isPublic.value ? { fieldName: 'marketCap2YearHigh', type: 'compactmoney', title: 'Market Cap (2-Year)', filter: 'compactMoney', exposureCategory: 'Market Cap', accessoryIcon: 'pencil' } : undefined,
    isPublic.value ? { fieldName: 'marketCapMostRecent', type: 'compactmoney', title: 'Market Cap (Recent)', filter: 'compactMoney', exposureCategory: 'Market Cap', accessoryIcon: 'pencil' } : undefined,
    { fieldName: 'revenueTTM', type: 'compactmoney', title: 'Revenue', classes: isPrivate.value ? 'sorted' : '', filter: 'compactMoney', exposureCategory: 'Company Financials' },
    isPrivate.value ? { fieldName: 'companyAssets', type: 'compactmoney', title: 'Assets', filter: 'compactMoney', exposureCategory: 'Company Financials', exposureMetricId: 2 } : undefined,
    { fieldName: 'totalDOLimit', type: 'compactmoney', title: 'D&O Limits', filter: 'compactMoney', url: (data) => `/account/${data.accountId}/policy-detail`, accessoryIcon: 'pencil' },
    { fieldName: 'totalABCLimit', type: 'compactmoney', title: 'ABC Limits', filter: 'compactMoney', },
    { fieldName: 'totalSideA', type: 'compactmoney', title: 'Side A Limits', filter: 'compactMoney' },
    { fieldName: 'totalRetention', type: 'compactmoney', title: 'Retention', filter: 'compactMoney' },
    { fieldName: 'totalPrimaryLimit', type: 'compactmoney', title: 'Primary Limit', filter: 'compactMoney' },
    { fieldName: 'maxPrimaryPPMAnnualized', type: 'compactmoney', title: 'Primary PPM', filter: 'compactMoney', annualized: true },
    { fieldName: 'firstLayerILF', type: 'text-right', title: '1st XS D&O Rate', filter: 'percentage' },
  ];
  return columns.filter(x => x) as Column<DOBenchmark>[];
});

const charts = computed((): Chart<DOBenchmark>[] => {
  const charts: (Chart<DOBenchmark> | undefined)[] = [
    isPublic.value ? {
      fieldName: 'marketCap2YearHigh',
      title: 'Market Capitalization (2-Year Max)',
    } : undefined,
    isPublic.value ? {
      fieldName: 'marketCapMostRecent',
      title: 'Market Capitalization (Most Recent)',
    } : undefined,
    {
      fieldName: 'revenueTTM',
      title: 'Revenue'
    },
    isPrivate.value ? {
      fieldName: 'companyAssets',
      title: 'Company Assets',
    } : undefined,
    {
      fieldName: 'totalDOLimit',
      title: 'D&O Limits'
    },
    {
      fieldName: 'totalABCLimit',
      title: 'ABC Limits'
    },
    {
      fieldName: 'totalSideA',
      title: 'Side A Limits'
    },
    {
      fieldName: 'totalRetention',
      title: 'Retention'
    },
    {
      fieldName: 'totalPrimaryLimit',
      title: 'Primary Limit'
    },
    {
      fieldName: 'maxPrimaryPPMAnnualized',
      title: 'Primary PPM (Annualized)'
    },
    {
      fieldName: 'firstLayerILF',
      title: '1st XS D&O Rate',
      isPercentage: true
    }
  ];
  return charts.filter(x => x) as Chart<DOBenchmark>[];
});


// ----------
// watchers
// ----------

watch(selectedAttributeTypes, () => refresh());
watch(selectedIndustries, () => refresh());

// ----------
// lifecycle
// ----------

onMounted(async () => refresh());

</script>

<template>
  <BenchmarkingTable :accountData="accountData" :otherData="otherData as Benchmark[]"
                     :averageData="averageData as Benchmark[]" :medianData="medianData as Benchmark[]"
                     :columns="columns as Column[]" :loading="isLoading" :titleText="titleText"
                     @needs-update="$emit('needs-update')"
  >
    <template v-slot:charts>
      <BenchmarkingCharts :accountData="accountData as Benchmark[]" :otherData="otherData as Benchmark[]" :charts="charts as Chart[]"/>
    </template>

    <template v-slot:filterbar>
      <slot name="filterbar"/>
      <ClientFilter v-model="selectedClients" :items="clients" @update:model-value="applyClientFilter"/>

      <FilterSlider v-if="isPublic"
                    title="Market Capitalization" description="Filter clients with a market capitalization between these values."
                    :values="marketCapRange"
                    :value-formatter-fn="val  => val ? compactMoney(val * 1000000) : ''"
                    min-title="Min (Millions)" max-title="Max (Millions)"
                    @update:model-value="marketCapChanged"
      />

      <FilterSlider v-if="isPrivate" title="Revenue"
                    :values="revenueRange" :value-formatter-fn="val => compactMoney(val)"
                    @update:model-value="revenueChanged"
      />

      <FilterSlider v-if="isPrivate" title="Company Assets"
                    :values="assetRange" :value-formatter-fn="val => compactMoney(val)"
                    @update:model-value="companyAssetsChanged"
      />

      <GenericFilter :types="industries" chip-title-field="label" v-model="selectedIndustries" title="Industries" data-test="industry-filter"/>
      <GenericFilter :types="attributeTypes" chip-title-field="label" v-model="selectedAttributeTypes" title="Client Attributes" data-test="client-type-filter"/>
      <AccountBenchmarkViewOptions :labels="appliedLabelDisplay" @change="applyViewOptions"/>
    </template>
  </BenchmarkingTable>
</template>
